import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTable, type SortableColumn } from '@/components/ui/sortable-table';
import { SectionTitle, StatTile } from '@/components/ui/stat-tile';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatNumber } from '@/lib/format';
import { formatStoreTime, storeToday } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import {
  ALL_CHANNELS,
  CHANNELS,
  useSalesSummary,
  type SalesSummary,
  type SummaryChannel,
  type SummaryDrop,
  type SummaryTender,
} from './sales-summary.api';

/**
 * Sales Summary — replaces the legacy `/finance/pos/summary`.
 *
 * The legacy page showed the same headline figures as the dashboard under the
 * same names and disagreed with it on most of them. These come from the
 * dashboard's own code, so the tiles reuse the dashboard's tooltips word for
 * word, and the page's job becomes showing how they are built: two statements
 * that add up to the cent, the payments and taxes behind them, and the drops.
 */

const ANY = 'ANY';

export function SalesSummaryPage() {
  const { t } = useTranslation();

  const today = storeToday();
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [businessId, setBusinessId] = useState(ANY);
  const [channel, setChannel] = useState<SummaryChannel>(ALL_CHANNELS);
  const [paymentView, setPaymentView] = useState<'tender' | 'card'>('tender');

  const { data: businesses } = useBusinesses();
  const report = useSalesSummary({
    start,
    end,
    businessId: businessId === ANY ? null : businessId,
    channel,
  });

  const data = report.data;
  const loading = report.isPending;
  const busy = report.isFetching;

  const channelKey = CHANNELS.find((entry) => entry.value === channel)?.key ?? 'all';

  /** A dashboard tooltip, plus when this figure is that tile's figure. */
  function hint(help: string, tile: string) {
    const note =
      channel === ALL_CHANNELS
        ? t('salesSummary.sameTile', { tile })
        : t('salesSummary.channelTile', {
            tile,
            channel: t(`salesSummary.channel_${channelKey}`),
          });
    return `${help} ${note}`;
  }

  const fields: FilterField[] = [
    {
      id: 'store',
      label: t('salesSummary.store'),
      value: businessId,
      onChange: setBusinessId,
      options: [
        { value: ANY, label: t('salesSummary.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    {
      id: 'channel',
      label: t('salesSummary.channel'),
      value: String(channel),
      resetValue: String(ALL_CHANNELS),
      sort: false,
      onChange: (value) => setChannel(Number(value) as SummaryChannel),
      options: CHANNELS.map((entry) => ({
        value: String(entry.value),
        label: t(`salesSummary.channel_${entry.key}`),
      })),
    },
    {
      id: 'start',
      label: t('salesSummary.from'),
      type: 'date',
      value: start,
      resetValue: start,
      onChange: setStart,
    },
    {
      id: 'end',
      label: t('salesSummary.to'),
      type: 'date',
      value: end,
      resetValue: end,
      onChange: setEnd,
    },
  ];

  const tile = { loading, busy };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('salesSummary.title')}
        description={t('salesSummary.subtitle')}
      />

      <FilterBar busy={busy} fields={fields} />

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatTile
          label={t('dashboard.orders')}
          value={formatNumber(data?.orders ?? 0)}
          tooltip={hint(t('dashboard.ordersHelp'), t('dashboard.orders'))}
          {...tile}
        />
        <StatTile
          label={t('dashboard.grossSales')}
          value={formatCurrency(data?.grossSales ?? 0)}
          tooltip={hint(t('dashboard.grossSalesHelp'), t('dashboard.grossSales'))}
          {...tile}
        />
        <StatTile
          label={t('dashboard.netSales')}
          value={formatCurrency(data?.netSales ?? 0)}
          tone="primary"
          tooltip={hint(t('dashboard.netSalesHelp'), t('dashboard.netSales'))}
          {...tile}
        />
        <StatTile
          label={t('dashboard.amountCollected')}
          value={formatCurrency(data?.amountCollected ?? 0)}
          tone="cash"
          tooltip={hint(
            t('dashboard.amountCollectedHelp'),
            t('dashboard.amountCollected')
          )}
          {...tile}
        />
        <StatTile
          label={t('dashboard.giftCardRedemptions')}
          value={formatCurrency(data?.giftCardRedemptions ?? 0)}
          tooltip={hint(
            t('dashboard.giftCardRedemptionsHelp'),
            t('dashboard.giftCardRedemptions')
          )}
          {...tile}
        />
        <StatTile
          label={t('dashboard.averageTicket')}
          value={formatCurrency(data?.averageTicket ?? 0)}
          tooltip={hint(
            t('dashboard.averageTicketHelp'),
            t('dashboard.averageTicket')
          )}
          {...tile}
        />
      </div>

      {report.error && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>
            {report.error instanceof Error
              ? report.error.message
              : t('salesSummary.failed')}
          </span>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <Statement
          title={t('salesSummary.collectedStatement')}
          loading={loading}
          busy={busy}
          lines={[
            { label: t('dashboard.grossSales'), value: data?.grossSales ?? 0 },
            {
              label: t('salesSummary.discounts'),
              value: data?.discounts ?? 0,
              sign: '−',
            },
            {
              label: t('salesSummary.tax'),
              value: data?.tax ?? 0,
              sign: '+',
              detail: t('salesSummary.taxDetail', {
                terminal: formatCurrency(data?.terminalTax ?? 0),
                unlogged: formatCurrency(data?.unloggedTax ?? 0),
              }),
            },
            {
              label: t('dashboard.amountCollected'),
              value: data?.amountCollected ?? 0,
              total: true,
            },
          ]}
        />
        <Statement
          title={t('salesSummary.netStatement')}
          loading={loading}
          busy={busy}
          lines={[
            { label: t('dashboard.grossSales'), value: data?.grossSales ?? 0 },
            {
              label: t('salesSummary.discounts'),
              value: data?.discounts ?? 0,
              sign: '−',
            },
            {
              label: t('salesSummary.refundsBeforeTax'),
              value: data?.refundsBeforeTax ?? 0,
              sign: '−',
              detail: t('salesSummary.refundsDetail', {
                refunds: formatCurrency(data?.refunds ?? 0),
                tax: formatCurrency(data?.refundTax ?? 0),
              }),
            },
            {
              label: t('dashboard.netSales'),
              value: data?.netSales ?? 0,
              total: true,
            },
          ]}
        />
        <Statement
          title={t('salesSummary.giftCards')}
          loading={loading}
          busy={busy}
          lines={[
            {
              label: t('salesSummary.giftCardsSold'),
              value: data?.giftCardsSold ?? 0,
              detail: t('salesSummary.giftCardsSoldDetail'),
            },
            {
              label: t('dashboard.giftCardRedemptions'),
              value: data?.giftCardRedemptions ?? 0,
              detail: t('salesSummary.giftCardRedemptionsDetail'),
            },
          ]}
        />
      </div>

      <section className="space-y-2">
        <Tabs
          value={paymentView}
          onValueChange={(value) => setPaymentView(value as 'tender' | 'card')}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <SectionTitle
              title={t('salesSummary.payments')}
              help={t('salesSummary.paymentsHelp')}
            />
            <TabsList className="sm:w-auto">
              <TabsTrigger
                value="tender"
                disabled={busy}
                className="sm:flex-none sm:px-4"
              >
                {t('dashboard.tenderType')}
              </TabsTrigger>
              <TabsTrigger
                value="card"
                disabled={busy}
                className="sm:flex-none sm:px-4"
              >
                {t('dashboard.cardType')}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="tender" className="mt-2">
            <PaymentTable
              rows={data?.tenderTypes ?? []}
              nameLabel={t('dashboard.tenderType')}
              loading={loading}
              busy={busy}
            />
          </TabsContent>
          <TabsContent value="card" className="mt-2">
            <PaymentTable
              rows={data?.cardTypes ?? []}
              nameLabel={t('dashboard.cardType')}
              loading={loading}
              busy={busy}
            />
          </TabsContent>
        </Tabs>
        {data && data.orders > 0 && (
          <p className="text-muted-foreground text-xs">
            {t('salesSummary.paymentsTotal', {
              collected: formatCurrency(data.amountCollected),
              orders: formatNumber(data.orders),
            })}
          </p>
        )}
      </section>

      <Taxes data={data} loading={loading} busy={busy} />

      <Drops data={data} loading={loading} busy={busy} />
    </div>
  );
}

// ---------------------------------------------------------------- statement

interface StatementLine {
  label: string;
  value: number;
  sign?: '+' | '−';
  detail?: string;
  total?: boolean;
}

/** A few lines that add up, with the result underlined. */
function Statement({
  title,
  lines,
  loading,
  busy,
}: {
  title: string;
  lines: StatementLine[];
  loading: boolean;
  busy: boolean;
}) {
  const refreshing = busy && !loading;
  return (
    <section className="bg-card relative rounded-xl border p-4">
      <BusyOverlay show={refreshing} size="sm" />
      <div className={cn(refreshing && 'opacity-60 transition-opacity')}>
        <h3 className="mb-2 text-sm font-semibold">{title}</h3>
        {loading ? (
          <div className="space-y-2.5">
            {lines.map((line) => (
              <Skeleton key={line.label} className="h-5 w-full" />
            ))}
          </div>
        ) : (
          <dl className="text-sm">
            {lines.map((line) => (
              <div
                key={line.label}
                className={cn(
                  'flex items-baseline justify-between gap-3 py-1.5',
                  line.total && 'mt-1 border-t pt-2 font-semibold'
                )}
              >
                <dt className="min-w-0">
                  <span className={cn(!line.total && 'text-muted-foreground')}>
                    {line.label}
                  </span>
                  {line.detail && (
                    <span className="text-muted-foreground block text-xs font-normal">
                      {line.detail}
                    </span>
                  )}
                </dt>
                <dd className="shrink-0 tabular-nums">
                  {line.sign && (
                    <span className="text-muted-foreground mr-1">{line.sign}</span>
                  )}
                  {formatCurrency(line.value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------- payments

function PaymentTable({
  rows,
  nameLabel,
  loading,
  busy,
}: {
  rows: SummaryTender[];
  nameLabel: string;
  loading: boolean;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const columns: SortableColumn<SummaryTender>[] = [
    {
      key: 'name',
      header: nameLabel,
      sortValue: (row) => row.name ?? '',
      render: (row) => <span className="font-medium">{row.name || '—'}</span>,
    },
    {
      key: 'orders',
      header: t('salesSummary.ordersColumn'),
      align: 'right',
      sortValue: (row) => row.orders,
      render: (row) => formatNumber(row.orders),
    },
    {
      key: 'collected',
      header: t('dashboard.amountCollected'),
      align: 'right',
      sortValue: (row) => row.amountCollected,
      render: (row) => formatCurrency(row.amountCollected),
    },
    {
      key: 'refunds',
      header: t('salesSummary.refundsColumn'),
      align: 'right',
      sortValue: (row) => row.refundTotal,
      render: (row) => (row.refundTotal ? formatCurrency(row.refundTotal) : '—'),
    },
    {
      key: 'net',
      header: t('salesSummary.afterRefundsColumn'),
      align: 'right',
      sortValue: (row) => row.salesTotal,
      render: (row) => formatCurrency(row.salesTotal),
    },
  ];

  return (
    <SortableTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.name ?? ''}
      loading={loading}
      busy={busy}
      pageSize={25}
      initialSort={{ key: 'collected', direction: 'desc' }}
      emptyMessage={t('salesSummary.noOrders')}
    />
  );
}

// -------------------------------------------------------------------- taxes

function Taxes({
  data,
  loading,
  busy,
}: {
  data: SalesSummary | undefined;
  loading: boolean;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const refreshing = busy && !loading;
  const taxes = data?.taxes ?? [];
  const refunded = taxes.reduce((sum, tax) => sum + tax.amountRefunded, 0);
  const net = taxes.reduce((sum, tax) => sum + tax.netAmount, 0);

  return (
    <section className="space-y-2">
      <SectionTitle
        title={t('salesSummary.taxes')}
        help={t('salesSummary.taxesHelp')}
      />
      {/* Positioned outside the clipping box: the overlay's spinner is
          sticky, and an overflow ancestor would pin it in place. */}
      <div className="relative rounded-xl">
        <BusyOverlay show={refreshing} size="sm" />
        <div className="bg-card overflow-hidden rounded-xl border">
          <div
            className={cn(
              'overflow-x-auto',
              refreshing && 'opacity-60 transition-opacity'
            )}
          >
            {loading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3].map((row) => (
                  <Skeleton key={row} className="h-5 w-full" />
                ))}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-xs">
                    <th className="px-4 py-2 text-left font-medium">
                      {t('salesSummary.taxColumn')}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t('salesSummary.applicableColumn')}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t('salesSummary.taxCollectedColumn')}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t('salesSummary.taxRefundedColumn')}
                    </th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t('salesSummary.taxNetColumn')}
                    </th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {taxes.map((tax) => (
                    <tr key={tax.taxName} className="border-b">
                      <td className="px-4 py-2">{tax.taxName}</td>
                      <td className="px-4 py-2 text-right">
                        {formatCurrency(tax.applicableSales)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {formatCurrency(tax.amountCollected)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {tax.amountRefunded ? formatCurrency(tax.amountRefunded) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {formatCurrency(tax.netAmount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b">
                    <td className="px-4 py-2">
                      {t('salesSummary.unloggedRow')}
                      <span className="text-muted-foreground block text-xs">
                        {t('salesSummary.unloggedRowDetail')}
                      </span>
                    </td>
                    <td className="text-muted-foreground px-4 py-2 text-right">—</td>
                    <td className="px-4 py-2 text-right">
                      {formatCurrency(data?.unloggedTax ?? 0)}
                    </td>
                    <td className="text-muted-foreground px-4 py-2 text-right">—</td>
                    <td className="px-4 py-2 text-right">
                      {formatCurrency(data?.unloggedTax ?? 0)}
                    </td>
                  </tr>
                  <tr className="font-semibold">
                    <td className="px-4 py-2">{t('salesSummary.total')}</td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 text-right">
                      {formatCurrency(data?.tax ?? 0)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {refunded ? formatCurrency(refunded) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {formatCurrency(net + (data?.unloggedTax ?? 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------- drops

type KeyedDrop = SummaryDrop & { key: string };

function Drops({
  data,
  loading,
  busy,
}: {
  data: SalesSummary | undefined;
  loading: boolean;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const drops: KeyedDrop[] = (data?.drops ?? []).map((drop, index) => ({
    ...drop,
    key: String(index),
  }));

  const columns: SortableColumn<KeyedDrop>[] = [
    {
      key: 'time',
      header: t('salesSummary.time'),
      sortValue: (drop) => drop.createdAt,
      render: (drop) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatStoreTime(drop.createdAt)}
        </span>
      ),
    },
    {
      key: 'store',
      header: t('salesSummary.store'),
      sortValue: (drop) => drop.businessName ?? '',
      render: (drop) => drop.businessName || '—',
    },
    {
      key: 'lane',
      header: t('salesSummary.lane'),
      sortValue: (drop) => drop.laneTag ?? '',
      render: (drop) => drop.laneTag || '—',
    },
    {
      key: 'employee',
      header: t('salesSummary.employee'),
      sortValue: (drop) => drop.employee ?? '',
      render: (drop) => drop.employee || '—',
    },
    {
      key: 'amount',
      header: t('salesSummary.amount'),
      align: 'right',
      sortValue: (drop) => drop.amount,
      render: (drop) => formatCurrency(drop.amount),
    },
  ];

  return (
    <section className="space-y-2">
      <SectionTitle
        title={t('salesSummary.drops')}
        help={t('salesSummary.dropsHelp')}
        actions={
          data && data.dropCount > 0 ? (
            <span className="text-sm font-medium tabular-nums">
              {t('salesSummary.dropsSummary', {
                drops: formatNumber(data.dropCount),
                amount: formatCurrency(data.dropsAmount),
              })}
            </span>
          ) : undefined
        }
      />
      <SortableTable
        rows={drops}
        columns={columns}
        rowKey={(drop) => drop.key}
        loading={loading}
        busy={busy}
        pageSize={25}
        initialSort={{ key: 'time', direction: 'asc' }}
        emptyMessage={t('salesSummary.noDrops')}
      />
    </section>
  );
}
